import React, { useState, useEffect, useRef } from 'react';
import { Container, PageTitle, CustomButtonV2 } from "../styles";
import { ChartSubtitle, ContentGraphics, CustomTitleGraphics, DashboardMain, SectionDivider, KpiGrid, KpiCard, KpiLabel, KpiValue, KpiSub, KpiDelta, TableWrap, RimTable, BadgeStatus, ProgressBarWrap, ProgressBar, ProgressFill, ProgressVal, BtnClear } from './style';
import { FilterIncidents } from '../../components/atoms/FilterIncidents';
import { CardGraphic } from '../../components/CardGraphic';
import { Grid, TablePagination } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SaveAltOutlinedIcon from '@mui/icons-material/SaveAltOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { useDownloadExcel } from "../../Hooks/useDownloadExcel";
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { BackdropLoading } from "../../components/BackdropLoading";

const ESTACIONES = [
    'Apiay', 'Araguaney', 'Banadía', 'Caucasia', 'Cogua', 'Cusiana',
    'El Porvenir', 'Galán', 'Güepajé', 'La Belleza', 'Malena', 'Mansilla',
    'Medina', 'Monterrey', 'Nazareth', 'Ocensa', 'Pozos Colorados',
    'Puerto Salgar', 'San Fernando', 'Sebastopol', 'Tenay', 'Vasconia',
    'Velásquez', 'Yumbo', 'Zarzal'
];

const RESPONSABLES = ['Ana Martínez', 'Carlos López', 'Diana Torres', 'Felipe Gómez', 'Laura Jiménez'];

// Generación de 100 incidentes dummy para que los gráficos tengan suficiente data al filtrar
const generateMockData = () => {
    const data = [];
    const categorias = ['Operacional', 'HSE', 'Calidad'];
    const tipos = ['Incidente', 'Cuasi incidente', 'Hallazgo'];
    const severidades = ['Crítica', 'Alta', 'Media', 'Baja'];
    const estados = ['Abierto', 'En proceso', 'Cerrado'];

    const start = new Date(2025, 0, 1).getTime();
    const currentDate = new Date();
    const end = currentDate.getTime();

    for (let i = 1; i <= 100; i++) {
        const estacion = ESTACIONES[Math.floor(Math.random() * ESTACIONES.length)];
        const categoria = categorias[Math.floor(Math.random() * categorias.length)];
        const tipo = tipos[Math.floor(Math.random() * tipos.length)];
        const severidad = severidades[Math.floor(Math.random() * severidades.length)];
        const estado = estados[Math.floor(Math.random() * estados.length)];

        const randomTime = start + Math.random() * (end - start);
        const dateObj = new Date(randomTime);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const fecha = `${year}-${month}-${day}`;

        const responsable = RESPONSABLES[Math.floor(Math.random() * RESPONSABLES.length)];

        data.push({
            id: `INC-${year}-${i.toString().padStart(3, '0')}`,
            estacion,
            tipo,
            categoria,
            severidad,
            estado,
            fecha,
            responsable,
            acciones: Math.floor(Math.random() * 5) + 1,
            avance: estado === 'Cerrado' ? 100 : (estado === 'Abierto' ? 0 : Math.floor(Math.random() * 80) + 10)
        });
    }
    // Ordenar más recientes primero
    return data.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
};

const MOCK_INCIDENTES = generateMockData();

export const IncidentsReport = (props) => {

    const { t } = useTranslation();
    const chartRef = useRef(null);
    const dashboardRef = useRef(null);

    // Filter States
    const [filtroDesde, setFiltroDesde] = useState('2025-01-01');
    const [filtroHasta, setFiltroHasta] = useState(new Date().toISOString().slice(0, 10));
    const [estacion, setEstacion] = useState([]);
    const [searchEstacion, setSearchEstacion] = useState('');
    const [filtroCategoria, setFiltroCategoria] = useState([]);
    const [filtroSeveridad, setFiltroSeveridad] = useState([]);
    const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Data State
    const [filteredIncidentes, setFilteredIncidentes] = useState(MOCK_INCIDENTES);

    const handleAplicarFiltros = () => {
        let filtrados = MOCK_INCIDENTES;

        if (filtroDesde) {
            filtrados = filtrados.filter(inc => new Date(inc.fecha) >= new Date(filtroDesde));
        }
        if (filtroHasta) {
            filtrados = filtrados.filter(inc => new Date(inc.fecha) <= new Date(filtroHasta));
        }
        if (estacion && estacion.length > 0) {
            filtrados = filtrados.filter(inc => estacion.includes(inc.estacion));
        }
        if (filtroCategoria && filtroCategoria.length > 0) {
            filtrados = filtrados.filter(inc => filtroCategoria.includes(inc.categoria));
        }
        if (filtroSeveridad && filtroSeveridad.length > 0) {
            filtrados = filtrados.filter(inc => filtroSeveridad.includes(inc.severidad));
        }

        setFilteredIncidentes(filtrados);
        setPage(0); // Reset page on filter
    };

    const fieldsConfig = {
        "N.° Incidente": 'id',
        "Estación": 'estacion',
        "Tipo": 'tipo',
        "Categoría": 'categoria',
        "Severidad": 'severidad',
        "Fecha registro": 'fecha',
        "Estado": 'estado',
        "Responsable": 'responsable',
        "Acciones": 'acciones',
        "Avance (%)": 'avance'
    };

    const { handleDownloadExcelList } = useDownloadExcel(filteredIncidentes, fieldsConfig, 'Reporte_Incidentes');

    const handleDownloadPDF = async () => {
        setLoading(true);
        const input = dashboardRef.current;
        if (!input) return;

        try {
            const canvas = await html2canvas(input, {
                scale: 1.2,
                useCORS: true,
                logging: false,
                backgroundColor: '#f8fafc',
                imageTimeout: 0,
                onclone: (clonedDoc) => {
                    clonedDoc.querySelectorAll('svg').forEach(svg => {
                        if (!svg.getAttribute('width')) {
                            svg.setAttribute('width', svg.getBoundingClientRect().width);
                            svg.setAttribute('height', svg.getBoundingClientRect().height);
                        }
                    });
                }
            });

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const marginX = 10;
            const usableWidth = pdfWidth - marginX * 2;
            const usableHeight = pdfHeight - marginX * 2;
            const pxPerMm = canvas.width / usableWidth;
            const pageHeightPx = usableHeight * pxPerMm;

            // 1. Obtener la posición real de cada hijo en el canvas
            const dashRect = input.getBoundingClientRect();
            const scale = canvas.width / input.offsetWidth;

            const sections = Array.from(input.children).map(child => {
                const rect = child.getBoundingClientRect();
                return {
                    top: (rect.top - dashRect.top) * scale,
                    height: rect.height * scale,
                };
            });

            // 2. Agrupar secciones en páginas respetando límites de componentes
            const pages = [];
            let currentPageTop = 0;
            let currentPageSections = [];

            for (const section of sections) {
                const relativeTop = section.top - currentPageTop;

                if (
                    relativeTop + section.height > pageHeightPx &&
                    currentPageSections.length > 0
                ) {
                    // Este componente no cabe → cerrar página actual y abrir nueva
                    pages.push({ top: currentPageTop, bottom: section.top });
                    currentPageTop = section.top;
                    currentPageSections = [section];
                } else {
                    currentPageSections.push(section);
                }
            }

            // Última página con la altura real del contenido restante
            if (currentPageSections.length > 0) {
                const last = currentPageSections[currentPageSections.length - 1];
                pages.push({ top: currentPageTop, bottom: last.top + last.height });
            }

            // 3. Recortar el canvas por cada página y agregar al PDF
            for (let i = 0; i < pages.length; i++) {
                if (i > 0) pdf.addPage();

                const { top, bottom } = pages[i];
                const sliceHeightPx = bottom - top;

                const pageCanvas = document.createElement('canvas');
                pageCanvas.width = canvas.width;
                pageCanvas.height = sliceHeightPx;
                pageCanvas.getContext('2d').drawImage(
                    canvas,
                    0, top, canvas.width, sliceHeightPx,
                    0, 0, canvas.width, sliceHeightPx
                );

                const sliceHeightMm = sliceHeightPx / pxPerMm;
                pdf.addImage(
                    pageCanvas.toDataURL('image/jpeg', 0.82),
                    'JPEG', marginX, marginX, usableWidth, sliceHeightMm
                );
            }

            pdf.save(`dashboard_incidentes_${new Date().toISOString().slice(0, 10)}.pdf`);
        } catch (error) {
            console.error("Error generating PDF", error);
        } finally {
            setLoading(false);
        }
    };

    // const handleDownloadPDF = async () => {
    //     setLoading(true);
    //     const input = dashboardRef.current;
    //     if (!input) return;

    //     try {
    //         const canvas = await html2canvas(input, {
    //             scale: 1.2,
    //             useCORS: true,
    //             logging: false,
    //             backgroundColor: '#f8fafc',
    //             imageTimeout: 0,
    //             onclone: (clonedDoc) => {
    //                 // Fix para SVGs de ApexCharts sin dimensiones explícitas
    //                 clonedDoc.querySelectorAll('svg').forEach(svg => {
    //                     if (!svg.getAttribute('width')) {
    //                         svg.setAttribute('width', svg.getBoundingClientRect().width);
    //                         svg.setAttribute('height', svg.getBoundingClientRect().height);
    //                     }
    //                 });
    //             }
    //         });

    //         const pdf = new jsPDF('p', 'mm', 'a4');
    //         const pdfWidth = pdf.internal.pageSize.getWidth();
    //         const pdfHeight = pdf.internal.pageSize.getHeight();
    //         const marginX = 10;
    //         const usableWidth = pdfWidth - marginX * 2;
    //         const usableHeight = pdfHeight - marginX * 2;
    //         const totalHeightMm = (canvas.height * usableWidth) / canvas.width;
    //         const totalPages = Math.ceil(totalHeightMm / usableHeight);
    //         const pxPerMm = canvas.width / usableWidth;
    //         const pageHeightPx = usableHeight * pxPerMm;

    //         for (let page = 0; page < totalPages; page++) {
    //             if (page > 0) pdf.addPage();

    //             const sourceY = page * pageHeightPx;

    //             // En la última página, usar solo los píxeles que quedan
    //             const remainingPx = canvas.height - sourceY;
    //             const sliceHeightPx = Math.min(pageHeightPx, remainingPx);

    //             const pageCanvas = document.createElement('canvas');
    //             pageCanvas.width = canvas.width;
    //             pageCanvas.height = sliceHeightPx;
    //             pageCanvas.getContext('2d').drawImage(
    //                 canvas,
    //                 0, page * pageHeightPx,
    //                 canvas.width, sliceHeightPx,
    //                 0, 0,
    //                 canvas.width, sliceHeightPx
    //             );

    //             // Ajustar el alto en el PDF proporcional al slice real
    //             const sliceHeightMm = (sliceHeightPx / pxPerMm);

    //             pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.82), 'JPEG', marginX, marginX, usableWidth, sliceHeightMm);
    //         }

    //         pdf.save(`dashboard_incidentes_${new Date().toISOString().slice(0, 10)}.pdf`);
    //     } catch (error) {
    //         console.error("Error generating PDF", error);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    // Colors adapted for light theme based on CSS logic
    const colors = {
        accent: '#3b9edd',
        green: '#2e7d32',
        red: '#d32f2f',
        yellow: '#f57f17',
        purple: '#7b1fa2'
    };

    const baseOptions = {
        chart: { background: 'transparent', toolbar: { show: false }, animations: { enabled: true, speed: 600 } },
        theme: { mode: 'light' },
        grid: { borderColor: 'rgba(0,0,0,0.06)', strokeDashArray: 4 },
    };

    // --- Dynamic Computations based on filteredIncidentes ---
    const generateMonthSequence = (startYear, startMonth, endYear, endMonth) => {
        const sequence = [];
        const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        let currentYear = startYear;
        let currentMonth = startMonth;

        while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
            const label = `${monthNames[currentMonth]} ${currentYear.toString().slice(2)}`;
            const key = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
            sequence.push({ label, key, registrados: 0, cerrados: 0 });

            currentMonth++;
            if (currentMonth > 11) {
                currentMonth = 0;
                currentYear++;
            }
        }
        return sequence;
    };

    const currentDateObj = new Date();
    const monthSequence = generateMonthSequence(2025, 0, currentDateObj.getFullYear(), currentDateObj.getMonth());
    const monthMap = {};
    monthSequence.forEach(m => { monthMap[m.key] = m; });

    let totalAcciones = 0;

    const countSeveridad = { 'Crítica': 0, 'Alta': 0, 'Media': 0, 'Baja': 0 };
    const countCategoriaTipo = {
        'Operacional': { 'Cuasi incidente': 0, 'Incidente': 0, 'Hallazgo': 0 },
        'HSE': { 'Cuasi incidente': 0, 'Incidente': 0, 'Hallazgo': 0 },
        'Calidad': { 'Cuasi incidente': 0, 'Incidente': 0, 'Hallazgo': 0 }
    };
    const countEstacion = {};
    const countResponsable = {};
    RESPONSABLES.forEach(r => countResponsable[r] = { 'Abierto': 0, 'En proceso': 0, 'Cerrado': 0 });

    let abiertosKPI = 0;
    let cerradosKPI = 0;
    let procesoKPI = 0;

    filteredIncidentes.forEach(inc => {
        const key = inc.fecha.substring(0, 7); // "YYYY-MM"
        if (monthMap[key]) {
            monthMap[key].registrados++;
            if (inc.estado === 'Cerrado') monthMap[key].cerrados++;
        }

        if (countSeveridad[inc.severidad] !== undefined) countSeveridad[inc.severidad]++;

        if (countCategoriaTipo[inc.categoria]) {
            countCategoriaTipo[inc.categoria][inc.tipo]++;
        }

        countEstacion[inc.estacion] = (countEstacion[inc.estacion] || 0) + 1;

        if (countResponsable[inc.responsable]) {
            countResponsable[inc.responsable][inc.estado]++;
        }

        totalAcciones += inc.acciones;

        if (inc.estado === 'Abierto') abiertosKPI++;
        if (inc.estado === 'Cerrado') cerradosKPI++;
        if (inc.estado === 'En proceso') procesoKPI++;
    });

    // Option Definitions
    const tendenciaOptions = {
        ...baseOptions,
        xaxis: { categories: monthSequence.map(m => m.label), axisBorder: { show: false }, tickAmount: 6 },
        colors: [colors.accent, colors.green],
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.28, opacityTo: 0.03, stops: [0, 100] } },
        stroke: { width: 2, curve: 'smooth' },
        dataLabels: { enabled: false },
    };
    const tendenciaSeries = [
        { name: 'Registrados', data: monthSequence.map(m => m.registrados) },
        { name: 'Cerrados', data: monthSequence.map(m => m.cerrados) },
    ];

    const severidadOptions = {
        ...baseOptions,
        labels: ['Crítica', 'Alta', 'Media', 'Baja'],
        colors: [colors.red, '#e65100', colors.yellow, colors.green],
        legend: { position: 'bottom' },
        plotOptions: { pie: { donut: { size: '62%', labels: { show: true, total: { show: true, label: 'Total' } } } } },
        dataLabels: { style: { colors: ['#ffffff'] } }
    };
    const severidadSeries = [countSeveridad['Crítica'], countSeveridad['Alta'], countSeveridad['Media'], countSeveridad['Baja']];

    const categoriaOptions = {
        ...baseOptions,
        chart: { ...baseOptions.chart, stacked: true },
        xaxis: { categories: ['Operacional', 'HSE', 'Calidad'], axisBorder: { show: false } },
        colors: [colors.purple, colors.accent, colors.green],
        plotOptions: { bar: { horizontal: false, borderRadius: 3 } },
        dataLabels: { enabled: false },
    };
    const categoriaSeries = [
        { name: 'Cuasi incidente', data: ['Operacional', 'HSE', 'Calidad'].map(c => countCategoriaTipo[c]['Cuasi incidente']) },
        { name: 'Incidente', data: ['Operacional', 'HSE', 'Calidad'].map(c => countCategoriaTipo[c]['Incidente']) },
        { name: 'Hallazgo', data: ['Operacional', 'HSE', 'Calidad'].map(c => countCategoriaTipo[c]['Hallazgo']) },
    ];

    const sortedEstaciones = Object.entries(countEstacion).sort((a, b) => b[1] - a[1]).slice(0, 8);
    const estacionXaxis = sortedEstaciones.map(e => e[0]);
    const estacionData = sortedEstaciones.map(e => e[1]);
    const estacionOptions = {
        ...baseOptions,
        xaxis: { categories: estacionXaxis.length ? estacionXaxis : ['Ninguna'], labels: { rotate: -20 }, axisBorder: { show: false } },
        colors: [colors.yellow],
        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
        dataLabels: { enabled: false },
    };
    const estacionSeries = [{ name: 'Incidentes', data: estacionData.length ? estacionData : [0] }];

    const estadoAccionesOptions = {
        ...baseOptions,
        labels: ['Cerradas', 'En proceso', 'Pendientes', 'Vencidas'],
        colors: [colors.green, colors.purple, colors.accent, colors.red],
        plotOptions: { radialBar: { hollow: { size: '25%' }, dataLabels: { total: { show: true, label: 'Acciones', formatter: () => totalAcciones + ' total' } } } },
        legend: { show: true, position: 'bottom' }
    };
    const estadoAccionesSeries = totalAcciones > 0 ? [Math.floor(totalAcciones * 0.4), Math.floor(totalAcciones * 0.3), Math.floor(totalAcciones * 0.2), Math.floor(totalAcciones * 0.1)] : [0, 0, 0, 0];

    const gaugeOptions = {
        ...baseOptions,
        labels: ['Cierre oportuno'],
        colors: [colors.green],
        plotOptions: { radialBar: { startAngle: -130, endAngle: 130, hollow: { size: '60%' }, dataLabels: { value: { formatter: v => v + '%' } } } },
    };
    const gaugeSeries = totalAcciones > 0 ? [Math.floor((cerradosKPI / (filteredIncidentes.length || 1)) * 100)] : [0];

    const tipoAccionOptions = {
        ...baseOptions,
        labels: ['Correctivas', 'Preventivas'],
        colors: [colors.accent, colors.purple],
        legend: { position: 'bottom' },
        dataLabels: { style: { colors: ['#ffffff'] } }
    };
    const tipoAccionSeries = totalAcciones > 0 ? [Math.floor(totalAcciones * 0.6), Math.floor(totalAcciones * 0.4)] : [0, 0];

    const responsablesOptions = {
        ...baseOptions,
        chart: { ...baseOptions.chart, stacked: true },
        xaxis: { categories: RESPONSABLES, axisBorder: { show: false } },
        colors: [colors.accent, colors.purple, colors.red],
        plotOptions: { bar: { horizontal: true, borderRadius: 3 } },
        dataLabels: { enabled: false },
    };
    const responsablesSeries = [
        { name: 'Abiertas', data: RESPONSABLES.map(r => countResponsable[r]['Abierto']) },
        { name: 'En proceso', data: RESPONSABLES.map(r => countResponsable[r]['En proceso']) },
        { name: 'Cerradas', data: RESPONSABLES.map(r => countResponsable[r]['Cerrado']) },
    ];

    const filteredEstaciones = ESTACIONES.filter(e => e.toLowerCase().includes(searchEstacion.toLowerCase()));

    return (
        <Container open={props.open} style={{ marginBottom: 20 }}>
            <BackdropLoading open={loading} />
            <div ref={dashboardRef} style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={3} lg={1} xl={1}>
                        <PageTitle>{t('dashboard.titleIncidents')}</PageTitle>
                    </Grid>

                    {/* Filtros */}
                    <Grid item xs={12} sm={9} lg={11} xl={11} mb={2}>
                        <FilterIncidents
                            filtroDesde={filtroDesde}
                            setFiltroDesde={setFiltroDesde}
                            filtroHasta={filtroHasta}
                            setFiltroHasta={setFiltroHasta}
                            searchEstacion={searchEstacion}
                            setSearchEstacion={setSearchEstacion}
                            estacion={estacion}
                            setEstacion={setEstacion}
                            isAutocompleteOpen={isAutocompleteOpen}
                            setIsAutocompleteOpen={setIsAutocompleteOpen}
                            filteredEstaciones={filteredEstaciones}
                            filtroCategoria={filtroCategoria}
                            setFiltroCategoria={setFiltroCategoria}
                            filtroSeveridad={filtroSeveridad}
                            setFiltroSeveridad={setFiltroSeveridad}
                            handleAplicarFiltros={handleAplicarFiltros}
                            handleClearFilters={() => {
                                setSearchEstacion('');
                                setEstacion([]);
                                setFiltroDesde('2025-01-01');
                                setFiltroHasta('2025-12-31');
                                setFiltroCategoria([]);
                                setFiltroSeveridad([]);
                                setFilteredIncidentes(MOCK_INCIDENTES);
                                setPage(0);
                            }}
                            handleDownloadPDF={handleDownloadPDF}
                        />
                    </Grid>
                </Grid>

                <SectionDivider>{t('dashboard.incidents.kpi')}</SectionDivider>

                <KpiGrid style={{ marginTop: '16px', marginBottom: '24px' }}>
                    <KpiCard colortype="blue">
                        <KpiLabel>Incidentes abiertos</KpiLabel>
                        <KpiValue colortype="blue">{abiertosKPI}</KpiValue>
                        <KpiSub><KpiDelta trend="up">▲ 2</KpiDelta> vs mes anterior</KpiSub>
                    </KpiCard>
                    <KpiCard colortype="green">
                        <KpiLabel>Incidentes cerrados</KpiLabel>
                        <KpiValue colortype="green">{cerradosKPI}</KpiValue>
                        <KpiSub><KpiDelta trend="up">▲ 6</KpiDelta> vs mes anterior</KpiSub>
                    </KpiCard>
                    <KpiCard colortype="yellow">
                        <KpiLabel>Investigaciones pendientes</KpiLabel>
                        <KpiValue colortype="yellow">{procesoKPI}</KpiValue>
                        <KpiSub><KpiDelta trend="neutral">→ 0</KpiDelta> sin cambio</KpiSub>
                    </KpiCard>
                    <KpiCard colortype="orange">
                        <KpiLabel>Acciones vencidas</KpiLabel>
                        <KpiValue colortype="orange">{Math.floor(totalAcciones * 0.1)}</KpiValue>
                        <KpiSub><KpiDelta trend="down">▼ 3</KpiDelta> requieren atención</KpiSub>
                    </KpiCard>
                    <KpiCard colortype="purple">
                        <KpiLabel>Tiempo prom. cierre</KpiLabel>
                        <KpiValue colortype="purple">12<span style={{ fontSize: '16px', fontWeight: 400 }}> d</span></KpiValue>
                        <KpiSub><KpiDelta trend="up">▲ mejoró 3d</KpiDelta></KpiSub>
                    </KpiCard>
                    <KpiCard colortype="green">
                        <KpiLabel>% Cierre oportuno</KpiLabel>
                        <KpiValue colortype="green">{gaugeSeries[0]}<span style={{ fontSize: '16px', fontWeight: 400 }}>%</span></KpiValue>
                        <KpiSub><KpiDelta trend="up">▲ +8%</KpiDelta> objetivo: 80%</KpiSub>
                    </KpiCard>
                </KpiGrid>

                <SectionDivider>{t("dashboard.incidents.incidents")}</SectionDivider>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6} lg={8}>
                        <ContentGraphics width='100%' height='100%' id='1'>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                <CustomTitleGraphics>{t("dashboard.incidents.tendency")}</CustomTitleGraphics>
                                <ChartSubtitle>{t("dashboard.incidents.tendencySubtitle")}</ChartSubtitle>
                            </div>
                            <CardGraphic
                                type="area"
                                options={tendenciaOptions}
                                series={tendenciaSeries}
                                chartRef={chartRef}
                            />
                        </ContentGraphics>
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                        <ContentGraphics width='100%' height='100%' id='2'>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                <CustomTitleGraphics>{t("dashboard.incidents.severity")}</CustomTitleGraphics>
                                <ChartSubtitle>{t("dashboard.incidents.severitySubtitle")}</ChartSubtitle>
                            </div>
                            <CardGraphic
                                type="donut"
                                options={severidadOptions}
                                series={severidadSeries}
                                chartRef={chartRef}
                            />
                        </ContentGraphics>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ContentGraphics width='100%' height='100%' id='3'>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                <CustomTitleGraphics>{t("dashboard.incidents.categoryType")}</CustomTitleGraphics>
                                <ChartSubtitle>{t("dashboard.incidents.categoryTypeSubtitle")}</ChartSubtitle>
                            </div>
                            <CardGraphic
                                type="bar"
                                options={categoriaOptions}
                                series={categoriaSeries}
                                chartRef={chartRef}
                            />
                        </ContentGraphics>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ContentGraphics width='100%' height='100%' id='4'>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                <CustomTitleGraphics>{t("dashboard.incidents.stations")}</CustomTitleGraphics>
                                <ChartSubtitle>{t("dashboard.incidents.stationsSubtitle")}</ChartSubtitle>
                            </div>
                            <CardGraphic
                                type="bar"
                                options={estacionOptions}
                                series={estacionSeries}
                                chartRef={chartRef}
                            />
                        </ContentGraphics>
                    </Grid>
                </Grid>

                <SectionDivider>{t("dashboard.incidents.correctiveActions")}</SectionDivider>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <ContentGraphics width='100%' height='100%' id='5'>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                <CustomTitleGraphics>{t("dashboard.incidents.statusCorrectiveActions")}</CustomTitleGraphics>
                                <ChartSubtitle>{t("dashboard.incidents.statusCorrectiveActionsSubtitle")}</ChartSubtitle>
                            </div>
                            <CardGraphic type="radialBar" options={estadoAccionesOptions} series={estadoAccionesSeries} chartRef={chartRef} />
                        </ContentGraphics>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <ContentGraphics width='100%' height='100%' id='6'>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                <CustomTitleGraphics>{t("dashboard.incidents.timelyClosure")}</CustomTitleGraphics>
                                <ChartSubtitle>{t("dashboard.incidents.timelyClosureSubtitle")}</ChartSubtitle>
                            </div>
                            <CardGraphic type="radialBar" options={gaugeOptions} series={gaugeSeries} chartRef={chartRef} />
                        </ContentGraphics>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <ContentGraphics width='100%' height='100%' id='7'>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                <CustomTitleGraphics>{t("dashboard.incidents.typeActions")}</CustomTitleGraphics>
                                <ChartSubtitle>{t("dashboard.incidents.typeActionsSubtitle")}</ChartSubtitle>
                            </div>
                            <CardGraphic type="pie" options={tipoAccionOptions} series={tipoAccionSeries} chartRef={chartRef} />
                        </ContentGraphics>
                    </Grid>

                    <Grid item xs={12}>
                        <ContentGraphics width='100%' height='100%' id='8'>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                <CustomTitleGraphics>{t("dashboard.incidents.assignmentsByPerson")}</CustomTitleGraphics>
                                <ChartSubtitle>{t("dashboard.incidents.assignmentsByPersonSubtitle")}</ChartSubtitle>
                            </div>
                            <CardGraphic type="bar" options={responsablesOptions} series={responsablesSeries} chartRef={chartRef} />
                        </ContentGraphics>
                    </Grid>
                </Grid>

                <SectionDivider>{t("dashboard.incidents.recentIncidents")}</SectionDivider>

                <ContentGraphics width='100%' height='100%'>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
                            <CustomTitleGraphics>{t("dashboard.incidents.incidentList")}</CustomTitleGraphics>
                            <ChartSubtitle>{t("dashboard.incidents.incidentListSubtitle")}</ChartSubtitle>
                        </div>
                        <CustomButtonV2 sx={{ maxWidth: '120px' }} onClick={handleDownloadExcelList} startIcon={<SaveAltOutlinedIcon />}>
                            {t('common.download')}
                        </CustomButtonV2>
                    </div>
                    <TableWrap style={{ height: '460px', overflowY: 'auto', padding: 0 }}>
                        <RimTable>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#ffffff' }}>
                                <tr>
                                    <th>N.° Incidente</th>
                                    <th>Estación</th>
                                    <th>Tipo</th>
                                    <th>Categoría</th>
                                    <th>Severidad</th>
                                    <th>Fecha registro</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                    <th>Avance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredIncidentes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(inc => {
                                    const sevClass = inc.severidad.toLowerCase().replace('í', 'i').replace('ó', 'o');
                                    const estClass = inc.estado.toLowerCase().replace(' ', '-');
                                    const progressColor = inc.avance === 100 ? '#2e7d32' : inc.avance >= 50 ? '#3b9edd' : '#f57f17';

                                    return (
                                        <tr key={inc.id}>
                                            <td style={{ fontSize: '12px', color: '#1d6fa4' }}>{inc.id}</td>
                                            <td style={{ color: '#1e293b', fontWeight: 500 }}>{inc.estacion}</td>
                                            <td>{inc.tipo}</td>
                                            <td>{inc.categoria}</td>
                                            <td><BadgeStatus status={sevClass}>{inc.severidad}</BadgeStatus></td>
                                            <td style={{ fontSize: '12px' }}>{inc.fecha}</td>
                                            <td><BadgeStatus status={estClass}>{inc.estado}</BadgeStatus></td>
                                            <td style={{ textAlign: 'center' }}>{inc.acciones}</td>
                                            <td style={{ minWidth: '100px' }}>
                                                <ProgressBarWrap>
                                                    <ProgressBar><ProgressFill fillcolor={progressColor} fillwidth={`${inc.avance}%`}></ProgressFill></ProgressBar>
                                                    <ProgressVal>{inc.avance}%</ProgressVal>
                                                </ProgressBarWrap>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filteredIncidentes.length === 0 && (
                                    <tr>
                                        <td colSpan="9" style={{ textAlign: 'center', padding: '24px' }}>No se encontraron incidentes con los filtros seleccionados</td>
                                    </tr>
                                )}
                            </tbody>
                        </RimTable>
                    </TableWrap>
                    <TablePagination
                        component="div"
                        count={filteredIncidentes.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50]}
                        labelRowsPerPage="Filas por página:"
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`}
                    />
                </ContentGraphics>
            </div>

        </Container>
    );
}